import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../api/client';
import type {
  RevenueOpportunity,
  RevenueScoreBreakdown,
  RevenueOpportunityAudience,
  RevenueAutomationDecision,
  RevenueAutomationMode,
  RevenueAutomationSettings,
  MarketOffersSummary,
  RevenueDemandSignal,
  RevenueOpportunityEventPlan,
  RevenueOpportunityResponse,
  RevenuePlayerList,
  RevenuePlayerListsResponse,
  RevenueRecoveredSummary,
  VenueMarketingInvite,
} from '../api/client';
import { REVENUE_AUDIENCES_PATH, audiencesLinkForBlock, audiencesLinkForList, audiencesLinkForQuestion } from '../utils/revenueLinks';
import { useModal } from '../context/ModalContext';
import { useAuth } from '../context/AuthContext';
import './RevenueOpportunitiesScreen.css';

const HORIZON_CHOICES = [3, 7, 14];
const RECOVERED_LOOKBACK_DAYS = 90;
// Blocks listed before "Show all"; a 14-day look-ahead can run to dozens.
// Short lists show in full rather than hiding one or two.
const BLOCKS_SHOWN = 6;
const BLOCKS_COLLAPSE_OVER = BLOCKS_SHOWN + 2;
// Below this many held-back players, a gap in booking rate is mostly noise.
const MIN_HELD_OUT_FOR_LIFT = 100;

function dollars(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Whole dollars for axis ticks and dense labels. */
function shortDollars(cents: number): string {
  const value = cents / 100;
  if (value >= 1000) return `$${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;
  return `$${Math.round(value)}`;
}

/** Whole dollars for headline figures, where cents are noise. */
function wholeDollars(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

function percent(fraction: number | null | undefined): string {
  if (fraction == null) return '—';
  return `${Math.round(fraction * 100)}%`;
}

function courtHours(value: number): string {
  return `${Number(value).toFixed(Number.isInteger(value) ? 0 : 1)} court-hours`;
}

/**
 * "Today" / "Tomorrow" / "In N days" by venue-local calendar date. Hours until
 * the start would call tomorrow morning "Today" when viewed the evening before.
 */
function leadTimeLabel(localDate: string, todayLocal: string | undefined, fallbackDays: number): string {
  const toDay = (value: string) => {
    const [year, month, day] = value.split('-').map(Number);
    return year && month && day ? Date.UTC(year, month - 1, day) / 86400000 : null;
  };
  const target = toDay(localDate);
  const today = todayLocal ? toDay(todayLocal) : null;
  const days = target != null && today != null ? Math.round(target - today) : Math.floor(fallbackDays);
  if (days < 1) return 'Today';
  if (days < 2) return 'Tomorrow';
  return `In ${days} days`;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Weekday for a venue-local YYYY-MM-DD, derived without touching the browser's
 * timezone. The date is already local to the venue; re-parsing it as an instant
 * would shift it for anyone viewing from elsewhere.
 */
function weekdayIndex(localDate: string): number | null {
  const [year, month, day] = localDate.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

function weekdayLabel(localDate: string): string {
  const index = weekdayIndex(localDate);
  return index == null ? localDate : WEEKDAYS[index];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Thursday, Sep 24" (or "Thu, Sep 24" short) for a venue-local YYYY-MM-DD. */
function friendlyDate(localDate: string, short = false): string {
  const index = weekdayIndex(localDate);
  const [, month, day] = localDate.split('-').map(Number);
  if (index == null || !month || !day) return localDate;
  const weekday = short ? WEEKDAYS[index].slice(0, 3) : WEEKDAYS[index];
  return `${weekday}, ${MONTHS[month - 1]} ${day}`;
}

/** "14:00" -> { time: '2', meridiem: 'PM' }; "06:30" -> { time: '6:30', meridiem: 'AM' }. */
function clockParts(hhmm: string): { time: string; meridiem: string } | null {
  const [hour, minute] = hhmm.split(':').map(Number);
  if (!Number.isFinite(hour)) return null;
  const h = hour % 24;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return { time: minute ? `${h12}:${String(minute).padStart(2, '0')}` : String(h12), meridiem: h >= 12 ? 'PM' : 'AM' };
}

/** "2–5 PM", or "10 AM–2 PM" when the block crosses noon. */
function hourRangeLabel(start: string, end: string): string {
  const a = clockParts(start);
  const b = clockParts(end);
  if (!a || !b) return `${start}–${end}`;
  if (a.meridiem === b.meridiem) return `${a.time}–${b.time} ${b.meridiem}`;
  return `${a.time} ${a.meridiem}–${b.time} ${b.meridiem}`;
}

/** Venue-local dates from start to end inclusive, as YYYY-MM-DD. */
function dateRange(startDate: string, endDate: string): string[] {
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  if (!sy || !ey) return [];
  const out: string[] = [];
  const cursor = new Date(Date.UTC(sy, sm - 1, sd));
  const end = Date.UTC(ey, em - 1, ed);
  while (cursor.getTime() <= end && out.length < 31) {
    out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

function priorityTier(score: number): { tier: 'high' | 'medium' | 'low'; label: string } {
  if (score >= 75) return { tier: 'high', label: 'High priority' };
  if (score >= 50) return { tier: 'medium', label: 'Medium priority' };
  return { tier: 'low', label: 'Low priority' };
}

/** Venue-local "HH:MM" (24-hour) for an instant. */
function localClock(iso: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: timezone,
  }).formatToParts(new Date(iso));
  const value = (type: string) => parts.find((part) => part.type === type)?.value || '00';
  return `${value('hour')}:${value('minute')}`;
}

/** The Audiences prompt that describes who usually plays in a block. */
function blockPrompt(opportunity: RevenueOpportunity): string {
  const index = weekdayIndex(opportunity.date);
  const day = index == null ? 'that day' : `${WEEKDAYS[index]}s`;
  const start = clockParts(opportunity.startHourLocal);
  const end = clockParts(opportunity.endHourLocal);
  const range = start && end
    ? `${start.time} ${start.meridiem} and ${end.time} ${end.meridiem}`
    : `${opportunity.startHourLocal} and ${opportunity.endHourLocal}`;
  return `Players who usually play on ${day} between ${range}`;
}

/** Venue-local clock time for an instant, independent of the viewer's zone. */
function localTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  }).format(new Date(iso));
}

/** A round axis ceiling (1, 2, 2.5 or 5 times a power of ten) above the max. */
function niceCeiling(value: number): number {
  if (value <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 2.5, 5, 10].find((candidate) => candidate * power >= value) || 10;
  return step * power;
}

type EventPlanState = {
  loading: boolean;
  data: RevenueOpportunityEventPlan | null;
  error: string | null;
};

type AudienceState = {
  loading: boolean;
  data: RevenueOpportunityAudience | null;
  error: string | null;
};

export default function RevenueOpportunitiesScreen() {
  const { showAlert, showConfirm } = useModal();

  const [horizonDays, setHorizonDays] = useState(7);
  const [report, setReport] = useState<RevenueOpportunityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [scoreShown, setScoreShown] = useState<string | null>(null);
  const [audiences, setAudiences] = useState<Record<string, AudienceState>>({});
  const [publishing, setPublishing] = useState<string | null>(null);
  const [published, setPublished] = useState<Record<string, string>>({});
  const [eventPlans, setEventPlans] = useState<Record<string, EventPlanState>>({});
  const [recovered, setRecovered] = useState<RevenueRecoveredSummary | null>(null);
  const [showAllBlocks, setShowAllBlocks] = useState(false);

  // The previous report stays on screen (dimmed) while a new look-ahead
  // loads, so switching 7 -> 14 days doesn't blank and re-flow the page.
  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setReport(await api.getRevenueOpportunities({ horizonDays }));
      setShowAllBlocks(false);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load revenue opportunities');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [horizonDays]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => api.getRevenueRecovered(RECOVERED_LOOKBACK_DAYS))
      .then((result) => { if (!cancelled) setRecovered(result); })
      .catch(() => { if (!cancelled) setRecovered(null); });
    return () => { cancelled = true; };
  }, []);

  const loadAudience = useCallback(async (opportunity: RevenueOpportunity) => {
    const key = opportunity.startUtc;
    setAudiences((prev) => ({ ...prev, [key]: { loading: true, data: null, error: null } }));
    try {
      const data = await api.previewRevenueOpportunityAudience({
        startUtc: opportunity.startUtc,
        endUtc: opportunity.endUtc,
        limit: 25,
      });
      setAudiences((prev) => ({ ...prev, [key]: { loading: false, data, error: null } }));
    } catch (err) {
      setAudiences((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          data: null,
          error: err instanceof Error ? err.message : 'Failed to load the audience',
        },
      }));
    }
  }, []);

  const compareEvent = useCallback(async (opportunity: RevenueOpportunity) => {
    const key = opportunity.startUtc;
    setEventPlans((prev) => ({ ...prev, [key]: { loading: true, data: null, error: null } }));
    try {
      const data = await api.planRevenueOpportunityEvent({
        startUtc: opportunity.startUtc,
        endUtc: opportunity.endUtc,
      });
      setEventPlans((prev) => ({ ...prev, [key]: { loading: false, data, error: null } }));
    } catch (err) {
      setEventPlans((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          data: null,
          error: err instanceof Error ? err.message : 'Failed to compare an event',
        },
      }));
    }
  }, []);

  const toggle = useCallback((opportunity: RevenueOpportunity) => {
    const key = opportunity.startUtc;
    setExpanded((prev) => {
      if (prev === key) return null;
      if (!audiences[key]) loadAudience(opportunity);
      return key;
    });
  }, [audiences, loadAudience]);

  const publish = useCallback(async (opportunity: RevenueOpportunity) => {
    const key = opportunity.startUtc;
    const audience = audiences[key]?.data;
    const size = audience?.returnedCount ?? 0;
    const confirmed = await showConfirm(
      'Publish this audience?',
      `${size} ${size === 1 ? 'player' : 'players'} will be published to a group at your `
      + 'booking provider. PickleCourts does not send anything — you message the group '
      + 'from your provider account.'
    );
    if (!confirmed) return;

    setPublishing(key);
    try {
      const result = await api.publishRevenueOpportunityAudience({
        startUtc: opportunity.startUtc,
        endUtc: opportunity.endUtc,
      });
      setPublished((prev) => ({ ...prev, [key]: result.audience.name }));
      await showAlert(
        'Audience published',
        `"${result.audience.name}" now holds ${result.audience.size} `
        + `${result.audience.size === 1 ? 'player' : 'players'}. Send to it from your provider account.`,
        'success'
      );
    } catch (err) {
      await showAlert(
        'Could not publish',
        err instanceof Error ? err.message : 'Publishing the audience failed',
        'error'
      );
    } finally {
      setPublishing(null);
    }
  }, [audiences, showAlert, showConfirm]);

  const totals = report?.totals;
  const collapsible = (report?.opportunities.length ?? 0) > BLOCKS_COLLAPSE_OVER;
  const coverage = report?.coverage;

  return (
    <DashboardLayout>
      <div className="rv">
        <header className="rv-header">
          <h1>Growth</h1>
          <span className="rv-powered">Powered by Audiences</span>
        </header>

        {report && !loadError && <RevenueHero
          report={report}
          horizonDays={horizonDays}
          onShowPlayers={(opportunity) => {
            if (expanded !== opportunity.startUtc) toggle(opportunity);
            document.getElementById(`rv-opp-${opportunity.startUtc}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          }}
        />}

        {report?.demo && (
          <div className="rv-demo" role="note">
            <strong>Sample data.</strong> This venue shows made-up numbers for testing and demos.
            None of it is real, and nothing is published or sent from here.
          </div>
        )}

        <AutomationCard timezone={report?.timezone ?? null} />

        {/* Audiences is also sold on its own, so it sits near the top rather
            than below the look-ahead. */}
        <AudiencesCard timezone={report?.timezone ?? null} />

        <div className="rv-section-head">
          <h2>Next {horizonDays} days</h2>
          <div className="rv-segmented" role="group" aria-label="Look ahead">
            {HORIZON_CHOICES.map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => setHorizonDays(days)}
                aria-pressed={horizonDays === days}
              >
                {days} days
              </button>
            ))}
          </div>
        </div>

        {loading && !report && <LoadingSkeleton />}

        {!loading && loadError && (
          <div className="rv-card rv-card--error rv-error">
            <div>
              <strong style={{ color: 'var(--rv-critical)' }}>Could not load opportunities</strong>
              <p className="rv-card-sub">{loadError}</p>
            </div>
            <button type="button" className="rv-btn" onClick={refresh}>Try again</button>
          </div>
        )}

        {!loadError && report && (
          <div className={`rv-stack ${loading ? 'rv-stack--reloading' : ''}`} aria-busy={loading}>
            <div className="rv-kpis">
              {/* Not all weak inventory lands in a block big enough to run a
                  promotion on, so the rows below can add up to less than the
                  total. Showing only the total invited the difference to be
                  read as a rounding error. */}
              <Kpi
                label="In promotable blocks"
                value={wholeDollars(totals?.promotableRevenueAtRiskCents)}
                hint={`${totals?.opportunityCount ?? 0} ${(totals?.opportunityCount ?? 0) === 1 ? 'block' : 'blocks'} worth acting on`}
              />
              <div className="rv-kpi">
                <div className="rv-kpi-label">Projected empty</div>
                <div className="rv-kpi-value">
                  {Number(totals?.projectedEmptyCourtHours ?? 0).toFixed(Number.isInteger(totals?.projectedEmptyCourtHours ?? 0) ? 0 : 1)}
                  <span className="rv-kpi-unit">court-hours</span>
                </div>
              </div>
              <div className="rv-kpi">
                <div className="rv-kpi-label">Projected utilization</div>
                <div className="rv-kpi-value">{percent(totals?.projectedUtilization)}</div>
                <Meter fraction={totals?.projectedUtilization ?? 0} label="Projected utilization" />
              </div>
              <RecoveredKpi summary={recovered} />
            </div>

            {/* A venue with no availability data is not the same as a full one. */}
            {coverage && !coverage.hasSellableInventory && (
              <div className="rv-card">
                <p className="rv-note rv-note--warn" style={{ margin: 0 }}>
                  No bookable availability was found for this venue in the next {horizonDays} days.
                  That is different from being fully booked — check that availability is syncing.
                </p>
              </div>
            )}

            {coverage?.hasSellableInventory && (
              <AtRiskByDayChart report={report} />
            )}

            <div className="rv-card">
              <h2 className="rv-card-title">Weak blocks to act on</h2>
              <p className="rv-card-sub">Highest priority first.</p>
              {coverage?.baselineModelled === false && coverage.hasSellableInventory && (
                <p className="rv-note">
                  Utilization here is read from your booking provider. Expected future pickup is
                  not modelled yet, so these projections assume no further bookings arrive.
                </p>
              )}

              {report.opportunities.length === 0 && (
                <div className="rv-panel">
                  <strong>Nothing at risk right now.</strong>
                  <p className="rv-card-sub">
                    {coverage?.hasSellableInventory
                      ? 'Every upcoming block is projected to fill above the threshold.'
                      : 'No availability data was found to measure.'}
                  </p>
                </div>
              )}

              {report.opportunities.length > 0 && (
                <div className="rv-opps-head" aria-hidden="true">
                  <span>When</span>
                  <span>Booked</span>
                  <span>At risk</span>
                  <span>Priority</span>
                </div>
              )}
              {report.opportunities.length > 0 && (
                <div className="rv-opps">
                  {(showAllBlocks || !collapsible ? report.opportunities : report.opportunities.slice(0, BLOCKS_SHOWN)).map((opportunity) => {
                    const key = opportunity.startUtc;
                    const audience = audiences[key];
                    const isOpen = expanded === key;
                    const publishedName = published[key];
                    const eventPlan = eventPlans[key];
                    const priority = priorityTier(opportunity.score);
                    const canPublish = (audience?.data?.returnedCount ?? 0) > 0;

                    return (
                      <div key={key} id={`rv-opp-${key}`} className={`rv-opp rv-opp--${priority.tier} ${isOpen || eventPlan ? 'rv-opp--open' : ''}`}>
                        <div className="rv-opp-main">
                          <div className="rv-opp-when">
                            <h3>
                              {friendlyDate(opportunity.date)}
                              <span className="rv-opp-time">{hourRangeLabel(opportunity.startHourLocal, opportunity.endHourLocal)}</span>
                            </h3>
                            <div className="rv-opp-when-sub">
                              {leadTimeLabel(opportunity.date, report.horizon?.startDate, opportunity.leadTimeDays)} · {courtHours(opportunity.projectedEmptyCourtHours)} empty
                            </div>
                          </div>
                          <div className="rv-opp-fill">
                            <div className="rv-opp-fill-line">
                              {percent(opportunity.projectedUtilization)} booked
                              {opportunity.usualUtilization != null && (
                                <span className="rv-opp-usual"> · usually {percent(opportunity.usualUtilization)}</span>
                              )}
                            </div>
                            <Meter fraction={opportunity.projectedUtilization} label="Projected utilization" />
                          </div>
                          <div className="rv-opp-money">
                            <strong>{wholeDollars(opportunity.revenueAtRiskCents)}</strong>
                            <span>at risk</span>
                          </div>
                          {opportunity.scoreBreakdown ? (
                            <button
                              type="button"
                              className={`rv-priority rv-priority--${priority.tier} rv-priority--button`}
                              title="How this priority is worked out"
                              aria-expanded={scoreShown === key}
                              onClick={() => setScoreShown(scoreShown === key ? null : key)}
                            >
                              <strong>{opportunity.score}</strong>
                              <span>{priority.label.replace(' priority', '')}</span>
                            </button>
                          ) : (
                            <div className={`rv-priority rv-priority--${priority.tier}`} title={priority.label}>
                              <strong>{opportunity.score}</strong>
                              <span>{priority.label.replace(' priority', '')}</span>
                            </div>
                          )}
                        </div>

                        {scoreShown === key && opportunity.scoreBreakdown && (
                          <ScoreWorking opportunity={opportunity} breakdown={opportunity.scoreBreakdown} />
                        )}

                        <div className="rv-opp-actions">
                          <button
                            type="button"
                            className="rv-link"
                            aria-expanded={isOpen}
                            onClick={() => toggle(opportunity)}
                          >
                            {isOpen ? 'Hide players' : 'Recommended players'}
                          </button>
                          <Link
                            className="rv-link"
                            to={audiencesLinkForBlock({
                              blockLabel: `${friendlyDate(opportunity.date)} · ${hourRangeLabel(opportunity.startHourLocal, opportunity.endHourLocal)}`,
                              prompt: blockPrompt(opportunity),
                              revenueAtRiskCents: opportunity.revenueAtRiskCents,
                            })}
                          >
                            Build a list in Audiences
                          </Link>
                          <button
                            type="button"
                            className="rv-link"
                            disabled={eventPlan?.loading}
                            onClick={() => compareEvent(opportunity)}
                          >
                            {eventPlan?.loading ? 'Comparing…' : 'Compare an event'}
                          </button>
                          {publishedName && (
                            <span className="rv-published">Published as “{publishedName}”</span>
                          )}
                        </div>

                        {eventPlan && !eventPlan.loading && (
                          <EventPlanPanel
                            plan={eventPlan.data}
                            error={eventPlan.error}
                            timezone={report.timezone}
                          />
                        )}

                        {isOpen && (
                          <div className="rv-panel">
                            {audience?.loading && <p className="rv-muted">Ranking players…</p>}
                            {audience?.error && <p style={{ color: 'var(--rv-critical)' }}>{audience.error}</p>}
                            {audience?.data && (
                              <AudiencePanel
                                audience={audience.data}
                                action={(
                                  // Publishing sits beside the list: an operator
                                  // approves players they have seen, not a count.
                                  <button
                                    type="button"
                                    className="rv-btn rv-btn--primary"
                                    disabled={publishing === key || !canPublish}
                                    onClick={() => publish(opportunity)}
                                  >
                                    {publishing === key ? 'Publishing…' : 'Publish audience'}
                                  </button>
                                )}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {collapsible && (
                <button
                  type="button"
                  className="rv-btn rv-more"
                  onClick={() => setShowAllBlocks((prev) => !prev)}
                >
                  {showAllBlocks
                    ? `Show top ${BLOCKS_SHOWN} only`
                    : `Show all ${report.opportunities.length} blocks`}
                </button>
              )}
            </div>
          </div>
        )}

        <div className="rv-grid-2">
          <DemandSignalCard />
          <MarketOffersCard />
        </div>

        <MemberInviteCard />
      </div>
    </DashboardLayout>
  );
}

/**
 * The page's anchor: what is at risk, the one block to act on first, and the
 * shape of the week. Everything below is detail for it.
 */
function RevenueHero({ report, horizonDays, onShowPlayers }: {
  report: RevenueOpportunityResponse;
  horizonDays: number;
  onShowPlayers: (opportunity: RevenueOpportunity) => void;
}) {
  const totals = report.totals;
  const top = report.opportunities[0] ?? null;

  if (!totals || (totals.revenueAtRiskCents ?? 0) <= 0) {
    return (
      <section className="rv-hero rv-hero--calm">
        <div className="rv-hero-main">
          <div className="rv-hero-kicker">Next {horizonDays} days</div>
          <p className="rv-hero-line">Nothing looks at risk right now. Every upcoming block is on track to fill.</p>
        </div>
      </section>
    );
  }

  return (
    <section className={`rv-hero ${top ? '' : 'rv-hero--calm'}`} aria-label="At a glance">
      <div className="rv-hero-main">
        <div className="rv-hero-kicker">At risk in the next {horizonDays} days</div>
        <div className="rv-hero-number">{wholeDollars(totals.revenueAtRiskCents)}</div>
        <p className="rv-hero-line">
          of court time is likely to go unused
          {totals.opportunityCount > 0 && (
            <>, across {totals.opportunityCount} {totals.opportunityCount === 1 ? 'block' : 'blocks'} worth acting on</>
          )}.
        </p>
        <UsualTotal report={report} />
      </div>

      {top && (
        <div className="rv-hero-next">
          <span className="rv-hero-next-label">Start here</span>
          <strong>{friendlyDate(top.date)}</strong>
          <span className="rv-hero-next-time">{hourRangeLabel(top.startHourLocal, top.endHourLocal)}</span>
          <span className="rv-hero-next-meta">
            {wholeDollars(top.revenueAtRiskCents)} at risk · {percent(top.projectedUtilization)} booked
          </span>
          <div className="rv-hero-actions">
            <button type="button" className="rv-btn rv-btn--accent" onClick={() => onShowPlayers(top)}>
              See recommended players
            </button>
            <Link
              className="rv-hero-link"
              to={audiencesLinkForBlock({
                blockLabel: `${friendlyDate(top.date)} · ${hourRangeLabel(top.startHourLocal, top.endHourLocal)}`,
                prompt: blockPrompt(top),
                revenueAtRiskCents: top.revenueAtRiskCents,
              })}
            >
              Build a list
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}

function LoadingSkeleton() {
  return (
    <div className="rv-stack" aria-busy="true" aria-label="Loading revenue opportunities">
      <div className="rv-kpis">
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className="rv-kpi">
            <span className="rv-skel rv-skel--label" />
            <span className="rv-skel rv-skel--value" />
          </div>
        ))}
      </div>
      <div className="rv-card"><span className="rv-skel rv-skel--chart" /></div>
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rv-kpi">
      <div className="rv-kpi-label">{label}</div>
      <div className="rv-kpi-value">{value}</div>
      {hint && <div className="rv-kpi-hint">{hint}</div>}
    </div>
  );
}

function Meter({ fraction, label }: { fraction: number; label: string }) {
  const clamped = Math.max(0, Math.min(1, Number(fraction) || 0));
  return (
    <div
      className="rv-meter"
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clamped * 100)}
    >
      <span style={{ width: `${clamped * 100}%` }} />
    </div>
  );
}

/**
 * Net recovered by deal codes: bookings paid with a campaign's code, less the
 * discount. Gross of players who would have booked anyway; only the holdout
 * separates those, so the label says "recovered", not "incremental".
 */
function RecoveredKpi({ summary }: { summary: RevenueRecoveredSummary | null }) {
  const measured = summary?.totals?.campaigns ?? 0;
  if (!summary || measured === 0) {
    return (
      <Kpi
        label="Recovered"
        value="—"
        hint="Shows once a deal-code campaign runs"
      />
    );
  }
  const { totals } = summary;
  return (
    <Kpi
      label="Recovered, net"
      value={wholeDollars(totals.netRecoveredCents)}
      hint={`${totals.bookingsRecovered} ${totals.bookingsRecovered === 1 ? 'booking' : 'bookings'} · ${wholeDollars(totals.discountCostCents)} in discounts · last ${RECOVERED_LOOKBACK_DAYS} days`}
    />
  );
}

type ChartDay = {
  date: string;
  cents: number;
  usualCents: number | null;
  utilization: number | null;
  usualUtilization: number | null;
  hours: number;
  blocks: number;
};

/**
 * How a block's priority is worked out: three factors, each scored 0-1 and
 * weighted, added up to the 0-100 number shown.
 */
function ScoreWorking({ opportunity, breakdown }: { opportunity: RevenueOpportunity; breakdown: RevenueScoreBreakdown }) {
  const { revenue, emptiness, leadTime } = breakdown;
  const days = Math.max(0, opportunity.leadTimeDays);
  const away = days < 1 ? `${Math.round(days * 24)} hours away` : `${days.toFixed(1)} days away`;
  const rows = [
    {
      label: 'Money at risk',
      detail: `${wholeDollars(opportunity.revenueAtRiskCents)} of a ${wholeDollars(revenue.referenceCents)} cap`,
      part: revenue,
    },
    {
      label: 'How empty',
      detail: `${percent(emptiness.value)} of court time projected empty`,
      part: emptiness,
    },
    {
      label: 'Timing',
      detail: `${away}; best about ${leadTime.idealDays} days out, worth nothing ${leadTime.toleranceDays} days either side`,
      part: leadTime,
    },
  ];
  const total = rows.reduce((sum, row) => sum + row.part.points, 0);
  return (
    <div className="rv-panel rv-score-working">
      <strong className="rv-panel-title">How this priority is worked out</strong>
      <table>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              <td>{row.detail}</td>
              <td className="rv-score-weight">{percent(row.part.value)} × {Math.round(row.part.weight * 100)} pts</td>
              <td className="rv-score-points">{row.part.points.toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" colSpan={3}>Priority (rounded)</th>
            <td className="rv-score-points">{Math.round(total)}</td>
          </tr>
        </tfoot>
      </table>
      <p className="rv-muted">75 and up is high priority, 50 to 74 medium.</p>
    </div>
  );
}

/** Days are within this of usual (5%, or $10) read as "about usual". */
function nearUsual(cents: number, usualCents: number): boolean {
  return Math.abs(cents - usualCents) <= Math.max(1000, usualCents * 0.05);
}

function signedDollars(cents: number): string {
  return `${cents > 0 ? '+' : '−'}${wholeDollars(Math.abs(cents))}`;
}

/**
 * The hero tile's comparison: what these same days usually leave unsold. Only
 * shown when every day with courts has a usual figure, so the two totals
 * cover the same days.
 */
function UsualTotal({ report }: { report: RevenueOpportunityResponse }) {
  const days = (report.days || []).filter((day) => day.capacityCourtHours > 0);
  if (days.length === 0 || days.some((day) => day.usualRevenueAtRiskCents == null)) return null;
  const usual = days.reduce((sum, day) => sum + (day.usualRevenueAtRiskCents || 0), 0);
  const now = days.reduce((sum, day) => sum + day.revenueAtRiskCents, 0);
  const diff = now - usual;
  const label = nearUsual(now, usual)
    ? 'about the same as usual'
    : `${signedDollars(diff)} vs. usual (${wholeDollars(usual)})`;
  return <div className={`rv-kpi-delta ${!nearUsual(now, usual) && diff > 0 ? 'rv-kpi-delta--worse' : ''}`}>{label}</div>;
}

/**
 * One bar per venue-local day: dollars of court time projected to go unused,
 * with a marker for what that weekday usually leaves open.
 */
function AtRiskByDayChart({ report }: { report: RevenueOpportunityResponse }) {
  const [active, setActive] = useState<number | null>(null);
  const days = useMemo<ChartDay[]>(() => {
    const blocksByDate = new Map<string, number>();
    for (const opportunity of report.opportunities) {
      blocksByDate.set(opportunity.date, (blocksByDate.get(opportunity.date) || 0) + 1);
    }
    if (report.days?.length) {
      return report.days.map((day) => ({
        date: day.date,
        cents: day.revenueAtRiskCents,
        usualCents: day.usualRevenueAtRiskCents,
        utilization: day.projectedUtilization,
        usualUtilization: day.usualUtilization,
        hours: day.projectedEmptyCourtHours,
        blocks: blocksByDate.get(day.date) || 0,
      }));
    }
    // Older API builds: sum the promotable blocks, with nothing to compare.
    const byDate = new Map<string, { cents: number; hours: number }>();
    for (const opportunity of report.opportunities) {
      const entry = byDate.get(opportunity.date) || { cents: 0, hours: 0 };
      entry.cents += opportunity.revenueAtRiskCents || 0;
      entry.hours += opportunity.projectedEmptyCourtHours || 0;
      byDate.set(opportunity.date, entry);
    }
    const range = dateRange(report.horizon.startDate, report.horizon.endDate);
    const dates = range.length ? range : Array.from(byDate.keys()).sort();
    return dates.map((date) => ({
      date,
      cents: byDate.get(date)?.cents || 0,
      hours: byDate.get(date)?.hours || 0,
      usualCents: null,
      utilization: null,
      usualUtilization: null,
      blocks: blocksByDate.get(date) || 0,
    }));
  }, [report]);

  if (days.length === 0) return null;
  const hasUsual = days.some((day) => day.usualCents != null);
  const max = niceCeiling(Math.max(...days.map((day) => Math.max(day.cents, day.usualCents ?? 0))));
  const ticks = [max, max / 2, 0];
  const sampleDays = report.coverage.baselineSampleDays;

  return (
    <div className="rv-card">
      <div className="rv-chart-head">
        <div>
          <h2 className="rv-card-title">Inventory at risk by day</h2>
          <p className="rv-card-sub">
            Court time projected to go unused, in dollars.
          </p>
        </div>
        {hasUsual && (
          <div className="rv-legend rv-legend--head" aria-hidden="true">
            <span className="rv-legend-swatch"><i className="rv-dot" style={{ background: 'var(--rv-navy)' }} />At risk</span>
            <span className="rv-legend-swatch"><i className="rv-usual-key" />Usual for that weekday</span>
          </div>
        )}
      </div>
      <div className="rv-bars">
        <div className="rv-bars-plot" onMouseLeave={() => setActive(null)}>
          {ticks.map((tick) => (
            <div key={tick} className="rv-bars-gridline" style={{ bottom: `${(tick / max) * 100}%` }}>
              <span>{shortDollars(tick)}</span>
            </div>
          ))}
          {days.map((day, index) => {
            const height = day.cents > 0 ? Math.max(2, (day.cents / max) * 100) : 0;
            const usualHeight = day.usualCents != null ? (day.usualCents / max) * 100 : null;
            const top = Math.max(height, usualHeight ?? 0);
            return (
              <div
                key={day.date}
                className="rv-bar-col"
                tabIndex={0}
                aria-label={`${weekdayLabel(day.date)} ${day.date}: ${dollars(day.cents)} at risk${day.usualCents != null ? `, usually ${dollars(day.usualCents)}` : ''}`}
                onMouseEnter={() => setActive(index)}
                onFocus={() => setActive(index)}
                onBlur={() => setActive(null)}
              >
                <div
                  className={`rv-bar ${day.cents > 0 ? '' : 'rv-bar--empty'}`}
                  style={{ height: `${height}%` }}
                />
                {usualHeight != null && (
                  <div className="rv-usual-mark" style={{ bottom: `${usualHeight}%` }} />
                )}
                {active === index && (
                  <div className="rv-tooltip" role="tooltip" style={{ bottom: `calc(${top}% + 10px)` }}>
                    <strong>{friendlyDate(day.date)}</strong>
                    <div>{dollars(day.cents)} at risk</div>
                    {day.usualCents != null && (
                      <div>Usually {dollars(day.usualCents)} on a {weekdayLabel(day.date)}</div>
                    )}
                    {day.utilization != null && (
                      <div>
                        {percent(day.utilization)} booked
                        {day.usualUtilization != null ? `, usually ${percent(day.usualUtilization)}` : ''}
                      </div>
                    )}
                    <div>{day.blocks} {day.blocks === 1 ? 'block' : 'blocks'} to act on · {courtHours(day.hours)} empty</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="rv-bar-labels" aria-hidden="true">
          {days.map((day) => {
            const diff = day.usualCents != null ? day.cents - day.usualCents : null;
            const near = day.usualCents != null && nearUsual(day.cents, day.usualCents);
            return (
              <div key={day.date}>
                <strong>{weekdayLabel(day.date).slice(0, 3)}</strong>
                {day.date.slice(5).replace('-', '/')}
                {diff != null && (
                  <span className={`rv-delta ${near ? '' : diff > 0 ? 'rv-delta--worse' : 'rv-delta--better'}`}>
                    {near ? 'usual' : signedDollars(diff)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {!hasUsual && (
        <p className="rv-note">
          A comparison with your usual {report.horizon.days > 1 ? 'days' : 'day'} appears after about two weeks of history
          {sampleDays != null && report.coverage.occupancySource === 'provider_availability'
            ? ` (${Math.min(sampleDays, 14)} of 14 days collected so far).`
            : '.'}
        </p>
      )}
    </div>
  );
}

/** Marks a feature that is part of a paid plan. */
function PaidBadge({ plan, on }: { plan: string; on?: boolean }) {
  return (
    <span
      className={`rv-paid ${on === false ? 'rv-paid--off' : ''}`}
      title={`Part of the paid ${plan} plan${on === false ? ', which is not on for this venue' : ''}`}
    >
      {on === false ? 'Paid, not on' : 'Paid'}
    </span>
  );
}

function AudiencePanel({ audience, action }: { audience: RevenueOpportunityAudience; action?: React.ReactNode }) {
  return (
    <>
      <div className="rv-panel-head">
        <p className="rv-card-sub">
          <strong className="rv-panel-title">Recommended players <PaidBadge plan="Audiences" /></strong>
          {audience.returnedCount} of {audience.candidateCount} likely players
          {audience.deliverability.suppressedUnsubscribed
            ? ` · ${audience.deliverability.suppressedUnsubscribed} unsubscribed and excluded`
            : ''}
        </p>
        {action}
      </div>
      {/* Ranking is not permission to contact. */}
      {!audience.deliverability.marketingConsentTracked && (
        <p className="rv-note rv-note--warn" style={{ margin: '0 0 12px' }}>
          These players have no marketing consent on record. Publishing is
          unavailable for this venue until consent is tracked.
        </p>
      )}
      <ol className="rv-players">
        {audience.audience.map((member, index) => (
          <li key={member.memberId || member.customerId || String(index)} className="rv-player">
            <strong>
              {member.displayName
                || [member.firstName, member.lastName].filter(Boolean).join(' ')
                || member.email
                || 'Player'}
            </strong>
            <span className="rv-player-score">
              <span className="rv-meter" aria-hidden="true">
                <span style={{ width: `${Math.max(0, Math.min(100, member.score))}%` }} />
              </span>
              score {member.score}
            </span>
            {member.reasons?.length > 0 && (
              <ul className="rv-player-reasons">
                {member.reasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </>
  );
}

/**
 * Would an event out-earn discounting this block? The comparison is against
 * the discount selling every court, so "event" is a strong claim and
 * "discount" only means an event is not shown to do better.
 */
function EventPlanPanel({
  plan,
  error,
  timezone,
}: {
  plan: RevenueOpportunityEventPlan | null;
  error: string | null;
  timezone: string;
}) {
  if (error) return <div className="rv-panel"><p style={{ color: 'var(--rv-critical)' }}>{error}</p></div>;
  if (!plan) return null;
  if (!plan.supported) {
    return <div className="rv-panel"><p className="rv-muted">{plan.reason}</p></div>;
  }
  const recommendation = plan.recommendation;
  const event = recommendation?.event;
  const shown = event || plan.candidates?.[0] || null;
  const scale = shown ? Math.max(shown.expectedRevenueCents, shown.discountCeilingCents, 1) : 1;

  return (
    <div className="rv-panel">
      <strong style={{ color: event ? 'var(--rv-good)' : 'var(--rv-text)' }}>{recommendation?.headline}</strong>
      {shown && (
        <>
          <p className="rv-card-sub">
            {shown.label.charAt(0).toUpperCase() + shown.label.slice(1)}
            {shown.ratingBand && (shown.ratingBand.min != null || shown.ratingBand.max != null)
              ? ` (${shown.ratingBand.min ?? ''}–${shown.ratingBand.max ?? ''})`
              : ''}
            {' · '}
            {localTime(shown.window.startUtc, timezone)}–{localTime(shown.window.endUtc, timezone)}
            {' · '}
            {shown.courts} {shown.courts === 1 ? 'court' : 'courts'} · up to {shown.capacity} players
            {' at '}
            {dollars(shown.pricePerPlayerCents)}
          </p>
          <div className="rv-compare">
            <div className="rv-compare-row">
              <span>Event, typical</span>
              <div className="rv-meter"><span style={{ width: `${(shown.expectedRevenueCents / scale) * 100}%` }} /></div>
              <b>{dollars(shown.expectedRevenueCents)}</b>
            </div>
            <div className="rv-compare-row">
              <span>{plan.percentOff}% discount, best case</span>
              <div className="rv-meter"><span style={{ width: `${(shown.discountCeilingCents / scale) * 100}%`, background: 'var(--rv-orange)' }} /></div>
              <b>{dollars(shown.discountCeilingCents)}</b>
            </div>
          </div>
          <ul className="rv-player-reasons rv-plan-reasons">
            {shown.reasons.map((reason) => <li key={reason}>{reason}</li>)}
          </ul>
        </>
      )}
      {event && plan.audience?.likelyPlayers != null && (
        <p className={`rv-note ${plan.audience.coversExpectedRegistrations ? '' : 'rv-note--warn'}`}>
          {plan.audience.likelyPlayers} likely players for that time
          {plan.audience.coversExpectedRegistrations ? '.' : ', fewer than a typical event draws.'}
        </p>
      )}
      <p className="rv-note">A recommendation only. Create the event in your booking provider.</p>
    </div>
  );
}

function formatDemandHour(hour: number): string {
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 === 0 ? 12 : hour % 12} ${suffix}`;
}

const HEAT_STEPS = ['var(--rv-seq-1)', 'var(--rv-seq-2)', 'var(--rv-seq-3)', 'var(--rv-seq-4)', 'var(--rv-seq-5)', 'var(--rv-seq-6)'];

// Kept short: the card sits near the top of the page.
const LISTS_SHOWN = 3;

// What Audiences can answer beyond building a list, as one-tap starters.
const ASK_STARTERS = [
  'Who were my top spenders last month?',
  'Whose memberships end in the next 30 days?',
  'Who used to play weekly but hasn’t come in for 60 days?',
];

function listTitle(list: RevenuePlayerList, timezone: string | null): string {
  if (list.origin !== 'weak_block' || !list.window || !timezone) return list.name;
  return `${friendlyDate(list.window.localDate)} · ${hourRangeLabel(
    localClock(list.window.startUtc, timezone),
    localClock(list.window.endUtc, timezone)
  )}`;
}

function share(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—';
}

/** What a list led to, in the plainest terms the data supports. */
function ListResult({ list }: { list: RevenuePlayerList }) {
  const result = list.result;
  if (!result) {
    return <span className="rv-muted">{list.publishedAt ? `Published ${shortDay(list.publishedAt)}` : 'Not published'}</span>;
  }
  if (result.status === 'not_published') return <span className="rv-muted">Not published yet</span>;
  if (result.status === 'upcoming') return <span className="rv-muted">Block is coming up</span>;
  if (result.status !== 'measured') return <span className="rv-muted">Results once check-ins sync</span>;
  return (
    <span className="rv-list-result">
      <strong>{result.came} of {result.players} came</strong> ({share(result.came, result.players)})
      {result.holdout && result.holdout.players > 0 && (
        <span className="rv-muted"> · {share(result.holdout.came, result.holdout.players)} of held-back players</span>
      )}
    </span>
  );
}

function shortDay(iso: string): string {
  const date = new Date(iso);
  return `${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

/**
 * Audiences on the Revenue page: a question box for the assistant, which
 * answers questions about visits, spending, memberships and events as well
 * as building lists, and the venue's saved lists. Lists made from a weak
 * block show who came in during it; a measured holdout sits beside that,
 * since only the comparison says the list made a difference.
 */
function AudiencesCard({ timezone }: { timezone: string | null }) {
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const ask = (text: string) => {
    if (text.trim()) navigate(audiencesLinkForQuestion(text));
  };
  const [data, setData] = useState<RevenuePlayerListsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let active = true;
    api.getRevenuePlayerLists()
      .then((result) => { if (active) setData(result); })
      .catch((err) => { if (active) setError(err instanceof Error ? err.message : 'Could not load player lists'); });
    return () => { active = false; };
  }, []);

  const lists = data?.lists ?? [];
  const shown = showAll ? lists : lists.slice(0, LISTS_SHOWN);
  const totals = data?.totals;

  return (
    <section className="rv-card">
      <div className="rv-card-head">
        <div>
          <h2 className="rv-card-title">Audiences <PaidBadge plan="Audiences" /></h2>
          <p className="rv-card-sub">Ask about your players’ visits, spending, memberships and events, or build a list to message.</p>
        </div>
        <Link className="rv-btn" to={REVENUE_AUDIENCES_PATH}>Open Audiences</Link>
      </div>

      <form
        className="rv-ask"
        onSubmit={(event) => { event.preventDefault(); ask(question); }}
      >
        <input
          type="text"
          aria-label="Ask Audiences"
          placeholder="Ask anything about your players"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
        />
        <button type="submit" className="rv-btn rv-btn--primary" disabled={!question.trim()}>Ask</button>
      </form>
      <p className="rv-ask-starters">
        <span>Try:</span>
        {ASK_STARTERS.map((starter) => (
          <button key={starter} type="button" className="rv-starter" onClick={() => ask(starter)}>
            {starter}
          </button>
        ))}
      </p>

      <h3 className="rv-subhead">Your lists</h3>

      {error && <p className="rv-note rv-note--warn">{error}</p>}
      {!data && !error && <span className="rv-skel rv-skel--label" style={{ marginTop: 12 }} />}

      {totals && totals.measured > 0 && (
        <p className="rv-lists-summary">
          Across {totals.measured} weak-block {totals.measured === 1 ? 'list' : 'lists'} with results,{' '}
          <strong>{totals.came} of {totals.players} players</strong> came in during the block.
        </p>
      )}

      {data && lists.length === 0 && (
        <div className="rv-panel" style={{ marginTop: 10 }}>
          <strong>No lists yet.</strong>
          <p className="rv-card-sub">
            Use “Build a list in Audiences” on a weak block, or publish its recommended players.
          </p>
        </div>
      )}

      {lists.length > 0 && (
        <ul className="rv-lists">
          {shown.map((list) => (
            <li key={list.id}>
              <Link className="rv-list" to={audiencesLinkForList(list.id)}>
                <span className="rv-list-main">
                  <strong>{listTitle(list, timezone)}</strong>
                  <span className="rv-list-meta">
                    <span className={`rv-tag ${list.origin === 'weak_block' ? 'rv-tag--block' : ''}`}>
                      {list.origin === 'weak_block' ? 'Weak block' : 'Built in Audiences'}
                    </span>
                    {list.size !== null && <span>{list.size.toLocaleString()} {list.size === 1 ? 'player' : 'players'}</span>}
                  </span>
                </span>
                <ListResult list={list} />
                <span className="rv-list-go" aria-hidden="true">›</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {lists.length > LISTS_SHOWN && (
        <button type="button" className="rv-btn rv-more" onClick={() => setShowAll((prev) => !prev)}>
          {showAll ? `Show ${LISTS_SHOWN} most recent` : `Show all ${lists.length} lists`}
        </button>
      )}
    </section>
  );
}

/**
 * When players browsing this area look for courts. Counts only: any hour with
 * fewer than the minimum number of devices is hidden, and other venues are
 * never named.
 */
function DemandSignalCard() {
  const [signal, setSignal] = useState<RevenueDemandSignal | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => api.getRevenueDemandSignal(28))
      .then((result) => { if (!cancelled) setSignal(result); })
      .catch(() => { if (!cancelled) setSignal(null); });
    return () => { cancelled = true; };
  }, []);

  if (!signal) return null;
  const areaAvailable = signal.areaAvailable === true;
  const cells = areaAvailable ? signal.area.cells : signal.venue.cells;
  const top = cells.slice(0, 6);
  const venueByCell = new Map(signal.venue.cells.map((cell) => [`${cell.dayOfWeek}:${cell.hour}`, cell.devices]));
  const about = (devices: number | undefined) => (devices == null ? '—' : `about ${devices}`);

  const byCell = new Map(cells.map((cell) => [`${cell.dayOfWeek}:${cell.hour}`, cell.devices]));
  const hours = cells.map((cell) => cell.hour);
  const firstHour = Math.min(6, ...hours);
  const lastHour = Math.max(22, ...hours);
  const hourRange = Array.from({ length: lastHour - firstHour + 1 }, (_, index) => firstHour + index);
  const maxDevices = Math.max(1, ...cells.map((cell) => cell.devices));
  const stepFor = (devices: number) => HEAT_STEPS[Math.min(HEAT_STEPS.length - 1, Math.floor((devices / maxDevices) * (HEAT_STEPS.length - 1) + 0.0001))];

  return (
    <div className="rv-card">
      <h2 className="rv-card-title">When players near you are looking</h2>
      <p className="rv-card-sub">
        Court times players tapped {areaAvailable ? 'in your area' : 'at your courts'}, last {signal.lookbackDays} days.
      </p>
      {!areaAvailable && (
        <p className="rv-note">
          Area comparison appears once more venues nearby are on PickleCourts.
        </p>
      )}
      {top.length === 0 ? (
        <p className="rv-note">
          {areaAvailable ? 'Not enough searches in your area yet.' : 'Not enough searches at your courts yet.'}
        </p>
      ) : (
        <>
          <div className="rv-heat" role="img" aria-label="Searches by day of week and hour">
            <div
              className="rv-heat-grid"
              style={{ gridTemplateColumns: `36px repeat(${hourRange.length}, minmax(12px, 1fr))` }}
            >
              <span />
              {hourRange.map((hour) => (
                <span key={hour} className="rv-heat-hour">
                  {hour % 3 === 0 ? formatDemandHour(hour).replace(' ', '').toLowerCase() : ''}
                </span>
              ))}
              {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                <React.Fragment key={day}>
                  <span className="rv-heat-day">{WEEKDAYS[day].slice(0, 3)}</span>
                  {hourRange.map((hour) => {
                    const devices = byCell.get(`${day}:${hour}`);
                    return (
                      <span
                        key={hour}
                        className="rv-heat-cell"
                        style={devices != null ? { background: stepFor(devices) } : undefined}
                      >
                        <span className="rv-tooltip">
                          <strong>{WEEKDAYS[day]} {formatDemandHour(hour)}</strong>
                          <span style={{ display: 'block' }}>
                            {devices != null ? `${about(devices)} players` : 'Too few to show'}
                          </span>
                        </span>
                      </span>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="rv-legend" aria-hidden="true">
            <span>Fewer</span>
            {HEAT_STEPS.map((step) => <i key={step} style={{ background: step }} />)}
            <span>More players</span>
            <span className="rv-legend-swatch" style={{ marginLeft: '10px' }}>
              <i style={{ background: 'var(--rv-neutral-bg)' }} /> too few to show
            </span>
          </div>
          <p className="rv-footnote">
            Hours with fewer than {signal.minDevicesPerCell} players are not shown, and counts are rounded down to
            the nearest {signal.deviceCountStep || 1}.
          </p>
          <details className="rv-details">
            <summary>Busiest times</summary>
            <table className="rv-table">
              <thead>
                <tr>
                  <th>Time</th>
                  {areaAvailable ? (
                    <>
                      <th>Players in your area</th>
                      <th>At your courts</th>
                    </>
                  ) : (
                    <th>Players looking at your courts</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {top.map((cell) => (
                  <tr key={`${cell.dayOfWeek}:${cell.hour}`}>
                    <td>{WEEKDAYS[cell.dayOfWeek]} {formatDemandHour(cell.hour)}</td>
                    {areaAvailable ? (
                      <>
                        <td>{about(cell.devices)}</td>
                        <td>{about(venueByCell.get(`${cell.dayOfWeek}:${cell.hour}`))}</td>
                      </>
                    ) : (
                      <td>{about(cell.devices)}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </>
      )}
    </div>
  );
}

/**
 * PickleCourts can't email a venue's member list: members agreed to hear from
 * the venue. The venue sends this link itself, members opt in on their own,
 * and the venue sees only how many did.
 */
function MemberInviteCard() {
  const [invite, setInvite] = useState<VenueMarketingInvite | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.getVenueMarketingInvite()
      .then((result) => { if (!cancelled) setInvite(result); })
      .catch(() => { if (!cancelled) setInvite(null); });
    return () => { cancelled = true; };
  }, []);

  if (!invite) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(invite.url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="rv-card rv-invite-card">
      <div>
        <h2 className="rv-card-title">Invite your members to PickleCourts offers</h2>
        <p className="rv-card-sub">
          Send this link from your own email or booking system. Members who opt in hear about open courts,
          including yours. We never see your member list.
        </p>
        <p className="rv-note">
          {invite.optedInCount === null
            ? `Fewer than ${invite.minimumReported} players have opted in through your link so far.`
            : `About ${invite.optedInCount} players have opted in through your link.`}
        </p>
      </div>
      <div className="rv-invite">
        <code>{invite.url}</code>
        <button type="button" className="rv-btn" onClick={copy}>
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
    </div>
  );
}

/**
 * Players near the venue who asked PickleCourts for offers, and who were (or,
 * in the preview, would have been) offered its publicly listed deals. A
 * random share is held out and never contacted, so bookings can be compared.
 */
function MarketOffersCard() {
  const [summary, setSummary] = useState<MarketOffersSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => api.getMarketOffersSummary(30))
      .then((result) => { if (!cancelled) setSummary(result); })
      .catch(() => { if (!cancelled) setSummary(null); });
    return () => { cancelled = true; };
  }, []);

  if (!summary || summary.campaigns.length === 0) return null;
  const preview = summary.campaigns.some((campaign) => campaign.mode === 'shadow');
  const rows = summary.campaigns.slice(0, 8);
  const reached = rows.reduce((sum, campaign) => sum + campaign.contacted, 0);
  const heldOut = rows.reduce((sum, campaign) => sum + campaign.heldOut, 0);
  const reachedBooked = rows.reduce((sum, campaign) => sum + campaign.contactedBooked, 0);
  const heldOutBooked = rows.reduce((sum, campaign) => sum + campaign.heldOutBooked, 0);
  const reachedRate = reached > 0 ? reachedBooked / reached : null;
  const heldOutRate = heldOut > 0 ? heldOutBooked / heldOut : null;
  const rateScale = Math.max(reachedRate ?? 0, heldOutRate ?? 0, 0.01);

  return (
    <div className="rv-card">
      <h2 className="rv-card-title">Nearby players offered your deals <PaidBadge plan="Revenue Autopilot" /></h2>
      <p className="rv-card-sub">
        {preview ? 'Preview: no emails have been sent yet. ' : ''}
        A random group is held back so you can compare how often each group books.
      </p>
      <div className="rv-compare" aria-label="Booking rate by group">
        <div className="rv-compare-row">
          <span className="rv-legend-swatch"><i className="rv-dot" style={{ background: 'var(--rv-blue)' }} />{preview ? 'Would reach' : 'Reached'}</span>
          <div className="rv-meter"><span style={{ width: `${((reachedRate ?? 0) / rateScale) * 100}%` }} /></div>
          <b>{percent(reachedRate)} booked</b>
        </div>
        <div className="rv-compare-row">
          <span className="rv-legend-swatch"><i className="rv-dot" style={{ background: 'var(--rv-orange)' }} />Held back</span>
          <div className="rv-meter"><span style={{ width: `${((heldOutRate ?? 0) / rateScale) * 100}%`, background: 'var(--rv-orange)' }} /></div>
          <b>{percent(heldOutRate)} booked</b>
        </div>
      </div>
      <p className="rv-note">
        {preview
          ? 'Nobody was contacted in the preview, so any gap between the two groups is chance.'
          : heldOut < MIN_HELD_OUT_FOR_LIFT
            ? `Early days: only ${heldOut} players held back so far, so this gap can still move a lot.`
            : reachedRate != null && heldOutRate != null && reachedRate > heldOutRate
              ? `Reached players booked ${Math.round((reachedRate - heldOutRate) * 100)} points more often than those held back.`
              : 'Reached players have not booked more often than those held back yet.'}
      </p>
      <table className="rv-table">
        <thead>
          <tr>
            <th>Deal date</th>
            <th>{preview ? 'Would reach' : 'Reached'}</th>
            <th>Held back</th>
            <th>Booked (reached / held back)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((campaign) => (
            <tr key={`${campaign.campaignId}:${campaign.mode}`}>
              <td>{friendlyDate(campaign.localDate, true)}</td>
              <td>{campaign.contacted}</td>
              <td>{campaign.heldOut}</td>
              <td>{campaign.contactedBooked} / {campaign.heldOutBooked}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const MODE_LABELS: Record<RevenueAutomationMode, string> = {
  off: 'Off',
  shadow: 'Shadow',
  act: 'On',
};

// The badge reflects what is saved, not an unsaved toggle.
const STATUS_LABELS: Record<RevenueAutomationMode, string> = {
  off: 'Off',
  shadow: 'Shadow mode',
  act: 'Live',
};

const MODE_HELP: Record<RevenueAutomationMode, string> = {
  off: 'Nothing is launched automatically.',
  shadow: 'Rules run and record what they would launch, but launch nothing.',
  act: 'Qualifying blocks get the same campaign you would approve here, within the weekly budget.',
};

const DECISION_LABELS: Record<RevenueAutomationDecision['decision'], string> = {
  launched: 'Launched',
  would_launch: 'Would launch',
  skipped: 'Skipped',
  failed: 'Failed',
};

const DECISION_PILL: Record<RevenueAutomationDecision['decision'], string> = {
  launched: 'rv-pill--launch',
  would_launch: 'rv-pill--launch',
  skipped: 'rv-pill--skip',
  failed: 'rv-pill--fail',
};

// Launches first, so a sweep's repeated skips never push them out of view.
const DECISION_ORDER: Record<RevenueAutomationDecision['decision'], number> = {
  launched: 0,
  would_launch: 0,
  failed: 1,
  skipped: 1,
};

// The rules as two short lines, so the form only opens when someone changes
// them.
function RulesSummary({ rules }: { rules: RevenueAutomationSettings['rules'] }) {
  const campaigns = `${rules.maxCampaignsPerWeek} ${rules.maxCampaignsPerWeek === 1 ? 'campaign' : 'campaigns'} a week`;
  const rows: Array<[string, string[]]> = [
    ['Acts on', [
      `Priority ${rules.minScore}+`,
      `${wholeDollars(rules.minRevenueAtRiskCents)}+ at risk`,
      `${rules.minLeadHours} hours to ${rules.maxLeadDays} days out`,
    ]],
    ['Offers', [
      `${rules.percentOff}% off`,
      `Up to ${campaigns}`,
      `${wholeDollars(rules.maxDiscountCostCentsPerWeek)} weekly discount budget`,
    ]],
  ];
  return (
    <dl className="rv-rules">
      {rows.map(([label, chips]) => (
        <div key={label} className="rv-rules-row">
          <dt>{label}</dt>
          <dd>{chips.join(' · ')}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Let rules launch campaigns. Off by default; shadow first so an operator can
 * read what the rules would have done before trusting them.
 */
function AutomationCard({ timezone }: { timezone: string | null }) {
  const { showAlert, showConfirm } = useModal();
  const { isSuperAdmin } = useAuth();
  const [switching, setSwitching] = useState(false);
  const [settings, setSettings] = useState<RevenueAutomationSettings | null>(null);
  const [draft, setDraft] = useState<RevenueAutomationSettings | null>(null);
  const [decisions, setDecisions] = useState<RevenueAutomationDecision[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [entitled, setEntitled] = useState(true);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ autopilot, ...current }, log] = await Promise.all([
        api.getRevenueAutomation(),
        api.getRevenueAutomationDecisions(),
      ]);
      setEntitled(autopilot?.entitled !== false);
      setSettings(current);
      setDraft(current);
      setDecisions(log.decisions);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load automation settings');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const saved = await api.saveRevenueAutomation({ mode: draft.mode, rules: draft.rules });
      setSettings(saved);
      setDraft(saved);
      setEditing(false);
    } catch (err) {
      await showAlert(
        'Could not save',
        err instanceof Error ? err.message : 'Saving automation settings failed',
        'error'
      );
    } finally {
      setSaving(false);
    }
  }, [draft, showAlert]);

  // PickleCourts staff turn the paid plan on or off for the venue here; the
  // server refuses anyone else.
  const switchPlan = async (enabled: boolean) => {
    const confirmed = await showConfirm(
      enabled ? 'Turn on Revenue Autopilot?' : 'Turn off Revenue Autopilot?',
      enabled
        ? 'This venue gets automation, deal codes and nearby-player offers. Only turn it on once they are paying or on an agreed trial.'
        : 'Automation, deal codes and nearby-player offers stop for this venue. Its settings are kept.'
    );
    if (!confirmed) return;
    setSwitching(true);
    try {
      await api.setRevenueAutopilot(enabled);
      await load();
    } catch (err) {
      await showAlert(
        'Could not change the plan',
        err instanceof Error ? err.message : 'Changing the plan failed',
        'error'
      );
    } finally {
      setSwitching(false);
    }
  };

  if (error) {
    return (
      <div className="rv-card">
        <h2 className="rv-card-title">Automation</h2>
        <p className="rv-card-sub" style={{ color: 'var(--rv-critical)' }}>{error}</p>
      </div>
    );
  }
  if (!draft || !settings) {
    // Holds the card's place so the page doesn't jump when it arrives.
    return (
      <section className="rv-card rv-auto" aria-busy="true">
        <h2 className="rv-card-title">Automation</h2>
        <span className="rv-skel rv-skel--label" style={{ marginTop: 10 }} />
      </section>
    );
  }

  const setRule = (key: keyof RevenueAutomationSettings['rules'], value: number) => {
    setDraft((prev) => (prev ? { ...prev, rules: { ...prev.rules, [key]: value } } : prev));
  };
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);
  const numberField = (
    label: string,
    key: keyof RevenueAutomationSettings['rules'],
    { scale = 1, prefix = '', suffix = '' }: { scale?: number; prefix?: string; suffix?: string } = {}
  ) => (
    <label className="rv-field">
      <span className="rv-field-label">{label}</span>
      <span className="rv-input">
        {prefix && <span className="rv-input-affix">{prefix}</span>}
        <input
          type="number"
          min={0}
          value={Number(draft.rules[key]) / scale}
          onChange={(event) => setRule(key, Math.round(Number(event.target.value) * scale))}
        />
        {suffix && <span className="rv-input-affix">{suffix}</span>}
      </span>
    </label>
  );
  const sortedDecisions = decisions
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => (
      DECISION_ORDER[a.entry.decision] - DECISION_ORDER[b.entry.decision] || a.index - b.index
    ))
    .slice(0, 8);
  const showRules = draft.mode !== 'off';
  const showForm = showRules && editing;

  return (
    <section className="rv-card rv-auto">
      <div className="rv-auto-head">
        <div>
          <h2 className="rv-card-title">
            Automation
            <span className={`rv-status rv-status--${settings.mode}`}>{STATUS_LABELS[settings.mode]}</span>
            <PaidBadge plan="Revenue Autopilot" on={entitled} />
          </h2>
          <p className="rv-card-sub">{MODE_HELP[draft.mode]}</p>
        </div>
        <div className="rv-segmented" role="group" aria-label="Automation mode">
          {(Object.keys(MODE_LABELS) as RevenueAutomationMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              aria-pressed={draft.mode === mode}
              // Staff may run shadow anywhere; it launches nothing.
              disabled={!entitled && mode !== 'off' && !(isSuperAdmin && mode === 'shadow')}
              onClick={() => setDraft((prev) => (prev ? { ...prev, mode } : prev))}
            >
              {MODE_LABELS[mode]}
            </button>
          ))}
        </div>
      </div>

      {!entitled && (
        <p className="rv-note rv-note--warn">
          Automation and deal codes are part of Revenue Autopilot, which isn’t on for this venue.
          {isSuperAdmin
            ? ' As PickleCourts staff you can still run Shadow here without it, or turn the plan on below.'
            : ' Contact PickleCourts to turn it on.'}
        </p>
      )}

      {showRules && !editing && (
        <div className="rv-auto-summary">
          <RulesSummary rules={draft.rules} />
          <button type="button" className="rv-btn rv-btn--small" onClick={() => setEditing(true)}>
            Edit rules
          </button>
        </div>
      )}

      {(showForm || sortedDecisions.length > 0) && (
        <div className={`rv-auto-body ${showForm && sortedDecisions.length > 0 ? 'rv-auto-body--split' : ''}`}>
          {showForm && (
            <div className="rv-auto-rules">
              <div className="rv-group">
                <div className="rv-group-title">Blocks that qualify</div>
                <div className="rv-fields">
                  {numberField('Priority at least', 'minScore')}
                  {numberField('At risk at least', 'minRevenueAtRiskCents', { scale: 100, prefix: '$' })}
                  {numberField('Starts after', 'minLeadHours', { suffix: 'hours' })}
                  {numberField('Starts within', 'maxLeadDays', { suffix: 'days' })}
                </div>
              </div>
              <div className="rv-group">
                <div className="rv-group-title">Offer and limits</div>
                <div className="rv-fields">
                  {numberField('Discount', 'percentOff', { suffix: '%' })}
                  {numberField('Campaigns per week', 'maxCampaignsPerWeek')}
                  {numberField('Weekly discount budget', 'maxDiscountCostCentsPerWeek', { scale: 100, prefix: '$' })}
                </div>
              </div>
              <div className="rv-checks">
                <label className="rv-check">
                  <input
                    type="checkbox"
                    checked={draft.rules.listPublicly === true}
                    onChange={(event) => {
                      const listPublicly = event.target.checked;
                      setDraft((prev) => (prev ? { ...prev, rules: { ...prev.rules, listPublicly } } : prev));
                    }}
                  />
                  <span>Also show offers to players browsing nearby on PickleCourts, labeled as a promoted deal</span>
                </label>
                <label className="rv-check">
                  <input
                    type="checkbox"
                    checked={draft.rules.syncedVenueDeals === true}
                    onChange={(event) => {
                      const syncedVenueDeals = event.target.checked;
                      setDraft((prev) => (prev ? { ...prev, rules: { ...prev.rules, syncedVenueDeals } } : prev));
                    }}
                  />
                  <span>If your schedule syncs from CourtReserve, offer a discount on bookings made through PickleCourts instead of sharing a member list. It counts against the discount budget.</span>
                </label>
              </div>
            </div>
          )}

          {sortedDecisions.length > 0 && (
            <div className="rv-auto-log">
              <div className="rv-group-title">Recent decisions</div>
              <ul className="rv-decisions">
                {sortedDecisions.map(({ entry }) => (
                  <li key={entry.id} className="rv-decision">
                    <span className={`rv-pill ${DECISION_PILL[entry.decision]}`}>{DECISION_LABELS[entry.decision]}</span>
                    <span className="rv-decision-text">
                      <span className="rv-decision-when">
                        {friendlyDate(entry.localDate)}
                        {timezone && ` · ${localTime(entry.startUtc, timezone)}–${localTime(entry.endUtc, timezone)}`}
                      </span>
                      <span className="rv-decision-reason">{entry.reason}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="rv-auto-foot">
        {isSuperAdmin && (
          <div className="rv-staff">
            <span>PickleCourts staff: the paid plan is <strong>{entitled ? 'on' : 'off'}</strong> for this venue.</span>
            <button
              type="button"
              className="rv-btn rv-btn--small"
              disabled={switching}
              onClick={() => switchPlan(!entitled)}
            >
              {switching ? 'Saving…' : entitled ? 'Turn off' : 'Turn on'}
            </button>
          </div>
        )}
        {dirty && <span className="rv-muted">Unsaved changes</span>}
        {editing && (
          <button
            type="button"
            className="rv-btn"
            disabled={saving}
            onClick={() => { setDraft(settings); setEditing(false); }}
          >
            Cancel
          </button>
        )}
        {(editing || dirty) && (
          <button
            type="button"
            className="rv-btn rv-btn--primary"
            disabled={!dirty || saving}
            onClick={save}
          >
            {saving ? 'Saving…' : 'Save automation'}
          </button>
        )}
      </div>
    </section>
  );
}
